/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Return a box that fits in the given dimensions
 */

import { isNumber } from '@zcode/utils'
import { ZOOM_DEFAULT } from '../../constants'
import type { Dimensions, MaybeNullBoundsObj, MaybeNullCoords } from '../../types'
import { getBoxCenter } from '../location'
import { getFittedBounds } from './getFittedBounds'
import { getFittedZoom } from './getFittedZoom'
import { isValidBounds } from './isValidBounds'
import { isValidDimensions } from './isValidDimensions'

interface Params {
  allowFloat?: boolean
  box?: MaybeNullBoundsObj | null
  center: MaybeNullCoords | undefined
  dimensions: Dimensions
  defaultZoom?: number
  minZoom?: number
  preventFit?: boolean
}

export const getFittedBoundsAndZoom = ({
  allowFloat,
  box,
  center,
  dimensions,
  defaultZoom,
  minZoom,
  preventFit,
}: Params) => {
  const hasValidBox = isValidBounds(box)
  const hasValidDimensions = isValidDimensions(dimensions)

  // if we do not have valid dimensions, just return the original values and hope for the best.
  if (preventFit || !hasValidBox || !hasValidDimensions) {
    return { box, zoom: defaultZoom || ZOOM_DEFAULT }
  }

  // if center is incorrect, get center from box

  if (!center || !isNumber(center.lat) || !isNumber(center.lng)) {
    center = getBoxCenter(box)
  }

  const fittedZoom = getFittedZoom({ bounds: box, dimensions, minZoom, allowFloat })
  const fittedBox = getFittedBounds({
    center,
    zoom: fittedZoom,
    dimensions,
  })

  return { box: fittedBox, zoom: fittedZoom }
}
